import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface CompanyProfile {
  id: string;
  company_name: string;
  cnpj: string | null;
  phone: string | null;
  sector: string | null;
  employee_count: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  website: string | null;
  culture_values: string | null;
  linkedin_url: string | null;
  instagram_url: string | null;
  plan: string | null;
  is_verified: boolean;
  profile_completed: boolean;
  credits: number;
  credits_expires_at: string | null;
  subscription_paid_until: string | null;
  jobs_per_month: number | null;
}

interface UseCompanyAccessResult {
  loading: boolean;
  companyId: string | null;
  companyProfile: CompanyProfile | null;
  isOwner: boolean;
  userId: string | null;
}

export const useCompanyAccess = (redirectOnFail: boolean = true): UseCompanyAccessResult => {
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    
    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!isMounted) return;

        if (!session) {
          if (redirectOnFail) {
            navigate('/entrar');
          }
          setLoading(false);
          return;
        }

        // Verificar se o contexto é empresa
        const userContext = localStorage.getItem('userContext');
        
        // Se não tem contexto definido ou é candidato, redirecionar para login da empresa
        if (userContext !== 'company') {
          if (redirectOnFail) {
            navigate('/entrar');
          }
          setLoading(false);
          return;
        }

        setUserId(session.user.id);

        // First, check if user is a company owner
        const { data: ownedCompany } = await supabase
          .from('company_profiles')
          .select('id, company_name, cnpj, phone, sector, employee_count, description, city, state, website, culture_values, linkedin_url, instagram_url, plan, is_verified, profile_completed, credits, credits_expires_at, subscription_paid_until, jobs_per_month')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (ownedCompany) {
          setCompanyId(ownedCompany.id);
          setCompanyProfile(ownedCompany);
          setIsOwner(true);
          setLoading(false);
          return;
        }

        // No access found - redirecionar para login
        if (redirectOnFail) {
          navigate('/entrar');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking company access:', error);
        if (isMounted && redirectOnFail) {
          navigate('/entrar');
        }
        if (isMounted) setLoading(false);
      }
    };

    checkAccess();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, redirectOnFail]);

  return { loading, companyId, companyProfile, isOwner, userId };
};
